/*
 * Design note for this page:
 * This access page keeps the same premium Command Secrets language while making
 * availability honest and explicit. The experience should feel controlled,
 * high-technology, and calm rather than vague or promotional.
 */

import { Button } from "@/components/ui/button";
import { ArrowLeft, Globe, Laptop, ShieldCheck, Smartphone } from "lucide-react";

function Access() {
  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(124,22,39,0.22),transparent_28%),linear-gradient(180deg,#06080d_0%,#090b10_54%,#05070b_100%)] text-white">
      <div className="absolute inset-0 opacity-30 pointer-events-none" aria-hidden="true">
        <div className="noise-overlay h-full w-full" />
      </div>

      <div className="relative container py-8 md:py-12">
        <div className="flex flex-col gap-6 rounded-[2rem] border border-white/10 bg-[rgba(255,255,255,0.03)] px-6 py-6 shadow-[0_40px_120px_rgba(0,0,0,0.45)] backdrop-blur md:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <a href="/" className="flex items-center gap-3 text-sm tracking-[0.32em] text-white">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(255,255,255,0.14)] bg-[rgba(131,12,28,0.24)]">
                <span className="font-display text-lg">C</span>
              </span>
              <span className="uppercase text-white/78">Command Secrets</span>
            </a>

            <Button variant="outline" className="w-fit border-white/12 text-xs uppercase tracking-[0.24em] text-white/78 hover:bg-white/8 hover:text-white" asChild>
              <a href="/">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Return home
              </a>
            </Button>
          </div>

          <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-end">
            <div className="max-w-3xl">
              <p className="eyebrow">Access options</p>
              <h1 className="mt-4 font-display text-4xl leading-tight text-white md:text-6xl">
                Command Secrets responds here with clear access status, not a silent loop.
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-8 text-white/68 md:text-lg">
                Immediate browser access is live now. Public Android and iPhone install links are not exposed on this website until the store-facing path is ready for real user and reviewer use. This page exists so anyone tapping the app-access CTA receives a concrete next step instead of a dead-end return.
              </p>
            </div>

            <div className="rounded-[1.8rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] p-6 shadow-[0_20px_80px_rgba(0,0,0,0.38)]">
              <p className="text-[11px] uppercase tracking-[0.32em] text-[var(--signal-soft)]">Immediate response</p>
              <h2 className="mt-4 font-display text-3xl text-white">Open the live web experience now.</h2>
              <p className="mt-4 text-sm leading-7 text-white/64">
                Users can review the system, pricing, and access structure immediately in-browser while native install distribution is finalized. This creates a visible, honest response path for visitors and platform reviewers.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Button className="bg-[var(--signal)] px-6 text-xs uppercase tracking-[0.24em] text-white hover:bg-[var(--signal-strong)]" asChild>
                  <a href="/#pricing">Review plans</a>
                </Button>
                <Button variant="outline" className="border-white/12 px-6 text-xs uppercase tracking-[0.24em] text-white/78 hover:bg-white/8 hover:text-white" asChild>
                  <a href="/#access">Read access notes</a>
                </Button>
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-[1.4rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-5">
              <Globe className="h-5 w-5 text-[var(--signal-soft)]" />
              <h3 className="mt-4 font-display text-2xl text-white">Browser access live</h3>
              <p className="mt-3 text-sm leading-7 text-white/64">
                Command Secrets can be opened right now on the website from mobile or desktop browser without waiting for a store redirect.
              </p>
            </div>

            <div className="rounded-[1.4rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-5">
              <Smartphone className="h-5 w-5 text-[var(--signal-soft)]" />
              <h3 className="mt-4 font-display text-2xl text-white">Android install path</h3>
              <p className="mt-3 text-sm leading-7 text-white/64">
                A direct public install link is not shown until the outward-facing store route is ready. Until then, this page states the availability honestly instead of pretending a download exists.
              </p>
            </div>

            <div className="rounded-[1.4rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-5">
              <Laptop className="h-5 w-5 text-[var(--signal-soft)]" />
              <h3 className="mt-4 font-display text-2xl text-white">Desktop review path</h3>
              <p className="mt-3 text-sm leading-7 text-white/64">
                Reviewers and customers can still inspect the offer, language support, and membership logic immediately through the desktop experience.
              </p>
            </div>

            <div className="rounded-[1.4rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-5">
              <ShieldCheck className="h-5 w-5 text-[var(--signal-soft)]" />
              <h3 className="mt-4 font-display text-2xl text-white">Clear reviewer signal</h3>
              <p className="mt-3 text-sm leading-7 text-white/64">
                The access CTA now produces a concrete response page with stated availability, which is materially better than sending users back into an unresponsive self-link.
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default Access;
